// The viewport guard (ui-map-interface.md §16.9, R1277).
//
// Every page of the client is a fixed, full-screen layout: `html` and
// `body` hide their overflow, and nothing scrolls the window itself. iOS
// can still leave the window scrolled after a rotation, most of all in a
// home-screen web app. The picture then stays where it was, but the hit
// test uses the offset, so every tap lands beside its control. A scroller
// inside the page still pans, which is why the fault reads as "the menus
// are dead".
//
// The guard puts the window back at 0,0 after every resize and every
// rotation: once on the next frame, and once more after the rotation
// animation ends, because iOS finishes the rotation after the first
// `resize`. A window already at 0,0 costs one comparison.
//
// The guard leaves the window alone while a field has focus: iOS scrolls
// the window on purpose to show a text box above the keyboard, and the
// keyboard also resizes the visual viewport.

import { CHROME_ROTATE_SETTLE_MS } from '../gen/presentation';
import { isField } from './pressroute';

/** True once `installViewportGuard` ran, so a second call adds nothing. */
let installed = false;

/** True when the window or the visual viewport stands away from 0,0. */
export function offsetOf(win: Pick<Window, 'scrollX' | 'scrollY'>,
  vv: Pick<VisualViewport, 'offsetLeft' | 'offsetTop' | 'scale'> | null): boolean {
  if (win.scrollX !== 0 || win.scrollY !== 0) return true;
  // A pinch zoom also moves the visual viewport. The pages forbid zoom, so
  // a scale of 1 with an offset is the stale offset of a rotation.
  return vv !== null && vv.scale === 1 && (vv.offsetLeft !== 0 || vv.offsetTop !== 0);
}

/** Scrolls the window back to 0,0 where it stands away from it. */
function reset(): void {
  const focus = document.activeElement;
  if (focus !== null && isField(focus)) return;
  if (offsetOf(window, window.visualViewport ?? null)) window.scrollTo(0, 0);
}

/** Resets on the next frame and again after the rotation settles. */
function settle(): void {
  requestAnimationFrame(reset);
  window.setTimeout(reset, CHROME_ROTATE_SETTLE_MS);
}

/** Installs the guard for the life of the page. */
export function installViewportGuard(): void {
  if (installed) return;
  installed = true;
  window.addEventListener('resize', settle);
  window.addEventListener('orientationchange', settle);
  window.visualViewport?.addEventListener('resize', settle);
  screen.orientation?.addEventListener?.('change', settle);
  // A home-screen app starts, and returns from the background, with the
  // window at an offset and no `resize` to report it.
  window.addEventListener('pageshow', settle);
  window.addEventListener('focus', settle);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') settle();
  });
  settle();
}
